import { ReactNode, useCallback, useEffect, useState } from 'react';
import { Outlet, useNavigate, useOutletContext } from 'react-router';
import { alpha } from '@mui/material/styles';

import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Label from 'src/components/label';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import Iconify from 'src/components/iconify';

export interface IRouterTabsObject {
  noDefault?: boolean;
  tabs: IRouterTabItem[];
}

export interface IRouterTabItem {
  children: ReactNode;
  label: string;
  count?: number;
  className?: string;
  path?: string;
  color?: any;
  icon?: any;
  nested?: IRouterTabsObject;
}

interface ICasRouterTabs {
  className?: string;
  tabList: IRouterTabItem[];
  baseUrl: string;
  asButtons?: boolean;
}

type IToogleButtonProps = {
  value: string | number | boolean | null;
  onChange: (_: React.SyntheticEvent, newValue: string) => void;
  tabList: IRouterTabItem[];
};

/*
  The tab content component is wrapped with this component by TabListToRouteList
  This component sets the active tab when it's loaded
  */
 
export function CasRouterTabWrapper({ label, children }: Pick<IRouterTabItem, 'label' | 'children'>) {
  const { setActiveTab } = useOutletContext<any>();

  useEffect(() => {
    setActiveTab(label);
  }, [label, setActiveTab]);

  return <>{children}</>;
}

function RenderButtons({ value, onChange, tabList }: IToogleButtonProps) {
  return (
    <ToggleButtonGroup exclusive value={value} onChange={onChange} color="primary">
      {tabList.map((tab: IRouterTabItem) => (
        <ToggleButton key={tab.label} value={tab.label} color={tab.color}>
          {tab.icon && <Iconify icon={tab.icon} width={20} height={20} />}
          {tab.label}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
}

function RenderTabs({ tabList, value, onChange, className }: any) {
  return (
    <Tabs
      value={value}
      onChange={onChange}
      sx={{
        px: 2.5,
        mb: 1,
        boxShadow: (theme) => `inset 0 -2px 0 0 ${alpha(theme.palette.grey[500], 0.08)}`,
      }}
      className={className}
    >
      {tabList.map((tab: any) => (
        <Tab
          key={tab.label}
          value={tab.label}
          label={tab.label}
          className={tab.className}
          iconPosition={tab.count ? 'end' : undefined}
          icon={tab.count ? <Label color="warning">{tab.count}</Label> : undefined}
        />
      ))}
    </Tabs>
  );
}

export default function CasRouterTabs({
  className,
  baseUrl,
  tabList,
  asButtons = false,
}: ICasRouterTabs) {
  const [activeTab, setActiveTab] = useState(null);
  const navigate = useNavigate();

  const onChange = useCallback((_: React.SyntheticEvent, newValue: string) => {
    if (newValue === null) {
      // Unselecting currently selected button - ignore
      return;
    }

    newValue = newValue.replace(' ', '');
    navigate(`${baseUrl}/${newValue}`);
  }, [baseUrl, navigate]);

  return (
    <div>
      {asButtons ? (
        <RenderButtons value={activeTab} tabList={tabList} onChange={onChange} />
      ) : (
        <RenderTabs value={activeTab} className={className} tabList={tabList} onChange={onChange} />
      )}
      <Outlet context={{ setActiveTab }} />
    </div>
  );
}
